import * as THREE from "three";

import { biomeFor } from "./biomes";

export interface RingEvent {
  type: "pass" | "miss";
  position: THREE.Vector3;
  ringIdx: number;
}

interface RingInstance {
  mesh: THREE.Mesh;
  inner: THREE.Mesh;
  zSpawn: number;
  idx: number;
  passed: boolean;
  scored: boolean;
  drift: { ax: number; ay: number; speed: number };
}

export class RingField {
  readonly group = new THREE.Group();
  private rings: RingInstance[] = [];
  private spacing = 26; // distance along Z between rings
  private spawnAhead = 28; // how many rings ahead of the player
  private radius = 3.0;
  private tube = 0.12;
  private nextZ: number;
  private rngState: number;

  private nextIdx = 0;

  constructor(seed = 1337) {
    this.rngState = seed >>> 0;
    this.nextZ = -this.spacing;
    for (let i = 0; i < this.spawnAhead; i++) {
      this.spawn(this.nextZ);
      this.nextZ -= this.spacing;
    }
  }

  reset(seed = 1337) {
    while (this.rings.length) this.dispose(this.rings.pop()!);
    this.rngState = seed >>> 0;
    this.nextZ = -this.spacing;
    this.nextIdx = 0;
    for (let i = 0; i < this.spawnAhead; i++) {
      this.spawn(this.nextZ);
      this.nextZ -= this.spacing;
    }
  }

  private rand() {
    // Mulberry32
    this.rngState = (this.rngState + 0x6d2b79f5) >>> 0;
    let t = this.rngState;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  private spawn(z: number) {
    const idx = this.nextIdx++;

    // Difficulty ramp: lateral spread + drift grow with progression.
    // Caps so it stays playable.
    const difficulty = Math.min(1.4, idx / 60);     // 0 → 1.4 over ~60 rings
    const lateralSpread = 12 + difficulty * 6;       // 12 → 20
    const verticalSpread = 5 + difficulty * 2;       // 5 → 7
    const driftScale = 1 + difficulty * 0.9;         // 1 → ~2

    // Biome color (lerped at transition windows)
    const { from, to, t } = biomeFor(idx);
    const c1 = from.ring.clone().lerp(to.ring, t);

    const ringGeo = new THREE.TorusGeometry(this.radius, this.tube, 8, 48);
    const mat = new THREE.MeshStandardMaterial({
      color: c1,
      emissive: c1,
      emissiveIntensity: 1.6,
      metalness: 0.3,
      roughness: 0.3,
    });
    const mesh = new THREE.Mesh(ringGeo, mat);

    const innerGeo = new THREE.RingGeometry(this.radius - 0.15, this.radius + 0.15, 48);
    const innerMat = new THREE.MeshBasicMaterial({
      color: c1,
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const inner = new THREE.Mesh(innerGeo, innerMat);
    mesh.add(inner);

    const lateral = (this.rand() - 0.5) * lateralSpread;
    const vertical = (this.rand() - 0.5) * verticalSpread;
    mesh.position.set(lateral, vertical, z);

    const drift = {
      ax: (this.rand() - 0.5) * 0.7 * driftScale,
      ay: (this.rand() - 0.5) * 0.4 * driftScale,
      speed: 0.4 + this.rand() * 0.6,
    };

    this.group.add(mesh);
    this.rings.push({ mesh, inner, zSpawn: z, idx, passed: false, scored: false, drift });
  }

  /** Most-recently-spawned ring index (highest idx). */
  get latestIdx(): number {
    return this.nextIdx - 1;
  }

  /** Index of the next ring the ship will encounter — used to anchor biome. */
  get upcomingIdx(): number {
    let best = this.nextIdx;
    for (const r of this.rings) {
      if (!r.scored && r.idx < best) best = r.idx;
    }
    return best;
  }

  private dispose(r: RingInstance) {
    this.group.remove(r.mesh);
    r.mesh.geometry.dispose();
    (r.mesh.material as THREE.Material).dispose();
    r.inner.geometry.dispose();
    (r.inner.material as THREE.Material).dispose();
  }

  /** Returns events generated this frame. */
  update(dt: number, t: number, shipPos: THREE.Vector3): RingEvent[] {
    const events: RingEvent[] = [];
    const passZ = shipPos.z; // ship moves toward -Z

    // gentle drift + rotation for life
    for (const r of this.rings) {
      r.mesh.rotation.z += dt * 0.4 * r.drift.speed;
      const baseX = r.mesh.position.x;
      const baseY = r.mesh.position.y;
      r.mesh.position.x = baseX + Math.sin(t * r.drift.speed + r.zSpawn) * r.drift.ax * dt;
      r.mesh.position.y = baseY + Math.cos(t * r.drift.speed * 0.7 + r.zSpawn) * r.drift.ay * dt;
    }

    // detect pass / miss as ship crosses ring plane
    for (const r of this.rings) {
      if (r.scored) continue;
      // Ship is at higher Z than ring initially (rings are at negative Z); ship moves to more-negative Z.
      // A ring is "behind" the ship when ringZ > shipZ (ring's Z is less negative => still in front),
      // wait — invert: ship.z starts at 0 and decreases. ring.z is negative. So ring is "passed" when ring.z > ship.z.
      if (r.mesh.position.z > passZ) {
        const dx = r.mesh.position.x - shipPos.x;
        const dy = r.mesh.position.y - shipPos.y;
        const dist = Math.hypot(dx, dy);
        const inside = dist < this.radius - 0.3;
        events.push({
          type: inside ? "pass" : "miss",
          position: r.mesh.position.clone(),
          ringIdx: r.idx,
        });
        r.scored = true;
      }
    }

    // recycle far-behind rings (ringZ > shipZ + buffer)
    const cullZ = passZ + 6;
    let recycled = 0;
    for (let i = this.rings.length - 1; i >= 0; i--) {
      if (this.rings[i].mesh.position.z > cullZ) {
        this.dispose(this.rings[i]);
        this.rings.splice(i, 1);
        recycled++;
      }
    }
    for (let i = 0; i < recycled; i++) {
      this.spawn(this.nextZ);
      this.nextZ -= this.spacing;
    }

    return events;
  }
}
